import { createTheme, type Theme } from '@mui/material/styles';
import {
  darkShadows,
  darkTokens,
  fontFamily,
  lightShadows,
  lightTokens,
  radii,
  type SoftShadows,
  type WeatherTokens,
} from './tokens';

declare module '@mui/material/styles' {
  interface Palette {
    weather: WeatherTokens;
  }
  interface PaletteOptions {
    weather: WeatherTokens;
  }
  interface Theme {
    softShadows: SoftShadows;
    radii: typeof radii;
  }
  interface ThemeOptions {
    softShadows: SoftShadows;
    radii: typeof radii;
  }
}

/** `light` / `dark` sunt modurile efective; alegerea utilizatorului poate fi și `auto`. */
export type ColorMode = 'light' | 'dark';

export function createAppTheme(mode: ColorMode): Theme {
  const tokens = mode === 'dark' ? darkTokens : lightTokens;
  const shadows = mode === 'dark' ? darkShadows : lightShadows;

  return createTheme({
    palette: {
      mode,
      weather: tokens,
      primary: { main: tokens.accent, contrastText: mode === 'dark' ? '#1c1c1e' : '#ffffff' },
      success: { main: tokens.ok },
      background: { default: tokens.bg, paper: tokens.surface },
      text: { primary: tokens.ink, secondary: tokens.soft, disabled: tokens.faint },
      divider: tokens.line,
    },
    softShadows: shadows,
    radii,
    shape: { borderRadius: radii.tile },
    typography: {
      fontFamily,
      // Cifrele stau pe coloană fixă — altfel valorile live „țopăie” la fiecare tick.
      allVariants: { fontVariantNumeric: 'tabular-nums' },
      h1: { fontWeight: 600, letterSpacing: '-0.02em' },
      button: { textTransform: 'none', fontWeight: 600 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: tokens.bg,
            color: tokens.ink,
            transition: 'background-color .5s ease, color .5s ease',
          },
          '@keyframes muiPulse': {
            '0%, 100%': { transform: 'scale(1)', opacity: 1 },
            '50%': { transform: 'scale(1.55)', opacity: 0 },
          },
          ':focus-visible': {
            outline: `2px solid ${tokens.accent}`,
            outlineOffset: 3,
          },
        },
      },
      MuiButtonBase: { defaultProps: { disableRipple: false } },
    },
  });
}
